import React, { useState, useEffect, useRef } from 'react';
import { Property } from '@/types';
import { XMarkIcon, GripVerticalIcon, LinkIcon } from '@/components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '@/firebase/firebaseConfig';

interface RankingModalProps {
    isOpen: boolean;
    onClose: () => void;
    likedProperties: (Property & { sourceCollection: string })[];
    onGenerateLink: (rankedIds: string[]) => void;
}

export const RankingModal: React.FC<RankingModalProps> = ({ isOpen, onClose, likedProperties, onGenerateLink }) => {
    const [rankedItems, setRankedItems] = useState(likedProperties);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const dragItemRef = useRef<HTMLLIElement | null>(null);

    useEffect(() => setRankedItems(likedProperties), [likedProperties]);

    if (!isOpen) return null;

    const handleDragStart = (e: React.DragEvent<HTMLLIElement>, index: number) => {
        setDraggingIndex(index);
        dragItemRef.current = e.currentTarget;
        e.dataTransfer.effectAllowed = 'move';
        setTimeout(() => { if (dragItemRef.current) dragItemRef.current.style.opacity = '0.5'; }, 0);
    };

    const handleDragEnter = (index: number) => {
        if (draggingIndex !== null && draggingIndex !== index) {
            const newRankedItems = [...rankedItems];
            const [draggedItem] = newRankedItems.splice(draggingIndex, 1);
            newRankedItems.splice(index, 0, draggedItem);
            setDraggingIndex(index);
            setRankedItems(newRankedItems);
        }
    };

    const handleDragEnd = () => {
        if (dragItemRef.current) dragItemRef.current.style.opacity = '1';
        setDraggingIndex(null);
        dragItemRef.current = null;
    };
    
    const handleGenerate = () => {
        onGenerateLink(rankedItems.map(p => p.id));
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full flex flex-col max-h-[90vh]">
                <header className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Ordena tus Inmuebles Favoritos</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-6 h-6" /></button>
                </header>
                <div className="p-6 overflow-y-auto">
                    <p className="text-sm text-gray-600 mb-4">Arrastra y suelta los inmuebles para ordenarlos por prioridad. El más importante debe quedar arriba.</p>
                    <ul className="space-y-2" onDragEnd={handleDragEnd}>
                        {rankedItems.map((item, index) => (
                            <li
                                key={item.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnter={() => handleDragEnter(index)}
                                className={`flex items-center p-2 bg-white border rounded-md cursor-grab transition-shadow ${draggingIndex === index ? 'opacity-50 shadow-lg' : 'border-gray-200'}`}
                            >
                                <GripVerticalIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <img src={item.images?.[0]} alt={item.codigo} className="w-12 h-12 object-cover rounded-md mr-3"/>
                                <div className="flex-grow">
                                    <p className="text-sm font-semibold text-gray-800">{item.tipoinmueble}</p>
                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{item.direccion}</p>
                                </div>
                                <span className="text-lg font-bold text-sky-700 ml-4">#{index + 1}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <footer className="bg-gray-50 px-6 py-4 border-t flex justify-end items-center space-x-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium bg-white border rounded-md">Cancelar</button>
                    <button onClick={handleGenerate} className="px-4 py-2 text-sm font-medium text-white rounded-md flex items-center" style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}>
                        <LinkIcon className="w-5 h-5 mr-2" />
                        Generar Enlace Ordenado
                    </button>
                </footer>
            </div>
        </div>
    );
};
